import React, { useState } from 'react';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import * as migrationService from '../../services/adminMemberMigration.service';

const ACTION_STYLE = {
  'create':                'bg-reset-sage/10 text-reset-sage',
  'already on new server': 'bg-gray-100 text-gray-500',
  'needs your decision':   'bg-amber-100 text-amber-700',
  'skip':                  'bg-gray-100 text-gray-400',
};

/**
 * Real GHL/FEA Create member migration — upload your exported CSV, review
 * every row before anything is created. Nothing here writes to a member
 * record; this is the review pass only, per Jenn's own "measure twice"
 * instruction for anything touching real member/billing data.
 */
export default function AdminMemberMigration({ embedded = false }) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const csvText = await file.text();
      setResult(await migrationService.previewMigration(csvText));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not read that file.');
    } finally {
      setLoading(false);
    }
  };

  const candidates = (result?.candidates || []).filter(c => c.isMigrateTagged);
  const visible = filter === 'all' ? candidates : candidates.filter(c => c.suggestedAction === filter);

  const content = (
    <>
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            {!embedded && <h1 className="font-serif text-2xl font-bold text-reset-brown">Member Migration</h1>}
            <p className="text-sm text-gray-500 mt-0.5">
              Upload your real GHL export &mdash; review only, nothing is created until you say so.
            </p>
          </div>
          <label className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors cursor-pointer">
            {loading ? 'Reading…' : 'Upload CSV'}
            <input type="file" accept=".csv" className="hidden" onChange={handleFileChosen} disabled={loading} />
          </label>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
        )}

        {result && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <SummaryCard label="Tagged 'migrate' in GHL" value={result.summary.migrateTagged} onClick={() => setFilter('all')} active={filter === 'all'} />
              <SummaryCard label="Ready to create" value={result.summary.readyToCreate} onClick={() => setFilter('create')} active={filter === 'create'} />
              <SummaryCard label="Needs your decision" value={result.summary.needsDecision} onClick={() => setFilter('needs your decision')} active={filter === 'needs your decision'} highlight={result.summary.needsDecision > 0} />
              <SummaryCard label="Already on server" value={result.summary.alreadyOnServer} onClick={() => setFilter('already on new server')} active={filter === 'already on new server'} />
            </div>

            <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50">
              {visible.length === 0 ? (
                <p className="text-sm text-gray-400 px-5 py-8 text-center">Nothing in this view.</p>
              ) : visible.map((c, i) => (
                <div key={i} className="px-5 py-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <span className="text-sm font-semibold text-reset-brown">{c.name || '(no name)'}</span>
                      <span className="text-xs text-gray-400 ml-2">{c.email || 'no email'}</span>
                    </div>
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${ACTION_STYLE[c.suggestedAction] || 'bg-gray-100 text-gray-500'}`}>
                      {c.suggestedAction}
                    </span>
                  </div>
                  {c.flag && <p className="text-xs text-amber-700 mt-1">{c.flag}</p>}
                  <p className="text-xs text-gray-400 mt-1">
                    created {c.created || '—'}
                    {c.founderTag && ' · founder'}
                    {c.activeTag && ' · active'}
                    {c.affiliateTag && ' · affiliate'}
                    {c.jan1Tag && ' · Jan 1'}
                  </p>
                  <p className="text-xs mt-0.5">
                    {c.dayNumberToResumeAt != null ? (
                      <span className="text-reset-sage font-medium">Resumes at daily email #{c.dayNumberToResumeAt} (last sent #{c.dayNumberLastSent})</span>
                    ) : (
                      <span className="italic text-gray-400">Day position not confirmed yet</span>
                    )}
                  </p>
                  {(c.address || c.dateOfBirth || c.additionalEmails || c.additionalPhones) && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {c.address && <>{c.address}</>}
                      {c.dateOfBirth && <> · DOB {c.dateOfBirth}</>}
                      {c.additionalEmails && <> · also: {c.additionalEmails}</>}
                      {c.additionalPhones && <> · also: {c.additionalPhones}</>}
                    </p>
                  )}
                  {c.lastNote && (
                    <p className="text-xs text-gray-500 italic mt-0.5">&ldquo;{c.lastNote}&rdquo;</p>
                  )}
                </div>
              ))}
            </div>

            <p className="text-xs text-gray-400 mt-4">
              This is the review pass only. Once you've settled the "needs your decision" rows (duplicate names — pick the correct current email)
              and filled in day numbers where you want them, tell me and I'll build the actual create-the-real-accounts step next.
            </p>
          </>
        )}
    </>
  );

  if (embedded) return content;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <AdminNav />
        {content}
      </div>
    </DashboardLayout>
  );
}

function SummaryCard({ label, value, onClick, active, highlight }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-2xl shadow-sm px-4 py-3 text-center transition-colors ${
        active ? 'bg-reset-terracotta text-white' : highlight ? 'bg-amber-50' : 'bg-white hover:bg-reset-cream'
      }`}
    >
      <p className={`text-xl font-bold ${active ? 'text-white' : 'text-reset-brown'}`}>{value}</p>
      <p className={`text-xs mt-0.5 ${active ? 'text-white/80' : 'text-gray-500'}`}>{label}</p>
    </button>
  );
}
